const OPEN_BODY_CLASS = 'nav-click-open';
const EDGE_MARGIN = 8;
const FALLBACK_PANEL_WIDTH = 280;

class ClickNav extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const { signal } = this.controller;

    this.lastToggle = null;
    this.onClickHandler = this.onClick.bind(this);
    this.onDocumentClickHandler = this.onDocumentClick.bind(this);
    this.onKeydownHandler = this.onKeydown.bind(this);
    this.onResizeHandler = this.closeAll.bind(this);
    this.addEventListener('click', this.onClickHandler, { signal });
    document.addEventListener('click', this.onDocumentClickHandler, { signal });
    document.addEventListener('keydown', this.onKeydownHandler, { signal });
    window.addEventListener('resize', this.onResizeHandler, { signal });
  }

  disconnectedCallback() {
    this.closeAll();
    this.removeEventListener('click', this.onClickHandler);
    document.removeEventListener('click', this.onDocumentClickHandler);
    document.removeEventListener('keydown', this.onKeydownHandler);
    window.removeEventListener('resize', this.onResizeHandler);
    this.controller?.abort();
    this.closest('.section-header-navigation')?.classList.remove('click-nav-section-open');
    this.updateScrim();
  }

  onClick(event) {
    if (!(event.target instanceof Element)) return;

    const toggle = event.target.closest('[data-click-nav-toggle]');
    if (toggle && this.contains(toggle)) {
      event.preventDefault();
      this.toggle(toggle);
      return;
    }

    const integration = event.target.closest(
      '[data-search-menu], [data-open-auth-sidebar], [data-cart-sidebar]'
    );
    if (integration && this.contains(integration)) {
      this.closeAll();
      return;
    }

    if (event.target.closest('a')) this.closeAll();
  }

  toggle(toggle) {
    if (toggle.getAttribute('aria-expanded') === 'true') {
      this.closeToggle(toggle);
      this.lastToggle = this.deepestOpenToggle();
      this.updateScrim();
      return;
    }

    this.closeGroup(toggle.dataset.clickNavGroup, toggle);
    const panel = this.panelFor(toggle);
    if (!panel) return;

    toggle.setAttribute('aria-expanded', 'true');
    toggle.classList.add('is-open');
    panel.hidden = false;
    panel.classList.add('is-open');
    this.lastToggle = toggle;
    this.positionPanel(toggle, panel);
    this.updateScrim();
  }

  closeGroup(group, exception) {
    if (!group) return;

    for (const toggle of this.toggles()) {
      if (
        toggle !== exception &&
        toggle.dataset.clickNavGroup === group &&
        toggle.getAttribute('aria-expanded') === 'true'
      ) {
        this.closeToggle(toggle);
      }
    }
  }

  closeToggle(toggle) {
    const panel = this.panelFor(toggle);
    toggle.setAttribute('aria-expanded', 'false');
    toggle.classList.remove('is-open');
    if (!panel) return;

    this.closeDescendants(panel.id);
    panel.hidden = true;
    panel.classList.remove('is-open', 'click-nav__panel--flipped');
    panel.style.removeProperty('--click-nav-panel-inline');
    panel.style.removeProperty('--click-nav-panel-block');
    panel.style.removeProperty('--click-nav-panel-height');
  }

  closeDescendants(group) {
    for (const toggle of this.toggles()) {
      if (
        toggle.dataset.clickNavGroup === group &&
        toggle.getAttribute('aria-expanded') === 'true'
      ) {
        this.closeToggle(toggle);
      }
    }
  }

  panelFor(toggle) {
    const id = toggle.getAttribute('aria-controls');
    if (!id) return null;
    return this.elementById(id);
  }

  parentToggleFor(panel) {
    const id = panel.dataset.clickNavParentTrigger;
    if (!id) return null;
    return this.elementById(id);
  }

  elementById(id) {
    for (const element of this.querySelectorAll('[id]')) {
      if (element.id === id) return element;
    }
    return null;
  }

  toggles() {
    return this.querySelectorAll('[data-click-nav-toggle]');
  }

  positionPanel(toggle, panel) {
    const triggerRect = toggle.getBoundingClientRect();
    const panelRect = panel.getBoundingClientRect();
    const panelWidth = panelRect.width || FALLBACK_PANEL_WIDTH;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const isRoot = toggle.hasAttribute('data-click-nav-root');
    const isRtl = getComputedStyle(this).direction === 'rtl';
    const containingPanel = toggle.closest('[data-click-nav-panel]');
    const anchor = containingPanel ? containingPanel.getBoundingClientRect() : triggerRect;
    let block = isRoot ? triggerRect.bottom : anchor.top;
    let inline = triggerRect.left;
    let flipped = false;

    if (isRoot) {
      inline = isRtl ? triggerRect.right - panelWidth : triggerRect.left;
      if (inline + panelWidth > viewportWidth - EDGE_MARGIN) {
        inline = triggerRect.right - panelWidth;
        flipped = true;
      }
    } else {
      const preferred = isRtl ? anchor.left - panelWidth : anchor.right;
      const alternate = isRtl ? anchor.right : anchor.left - panelWidth;
      const preferredFits = preferred >= EDGE_MARGIN && preferred + panelWidth <= viewportWidth - EDGE_MARGIN;
      const alternateFits = alternate >= EDGE_MARGIN && alternate + panelWidth <= viewportWidth - EDGE_MARGIN;

      if (preferredFits) {
        inline = preferred;
      } else if (alternateFits) {
        inline = alternate;
        flipped = true;
      } else {
        const preferredSpace = isRtl ? anchor.left : viewportWidth - anchor.right;
        const alternateSpace = isRtl ? viewportWidth - anchor.right : anchor.left;
        inline = preferredSpace >= alternateSpace ? preferred : alternate;
        flipped = preferredSpace < alternateSpace;
      }
    }

    inline = Math.min(
      Math.max(inline, EDGE_MARGIN),
      Math.max(EDGE_MARGIN, viewportWidth - panelWidth - EDGE_MARGIN)
    );
    block = Math.max(EDGE_MARGIN, block);
    const availableHeight = Math.max(1, viewportHeight - block - EDGE_MARGIN);
    const inlineStart = isRtl ? viewportWidth - inline - panelWidth : inline;

    panel.classList.toggle('click-nav__panel--flipped', flipped);
    panel.style.setProperty('--click-nav-panel-inline', `${inlineStart}px`);
    panel.style.setProperty('--click-nav-panel-block', `${block}px`);
    panel.style.setProperty('--click-nav-panel-height', `${availableHeight}px`);
  }

  onDocumentClick(event) {
    if (!this.contains(event.target)) this.closeAll();
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      const restore = this.deepestOpenToggle() || this.lastToggle;
      const hadOpenPanel = this.hasOpenPanel();
      this.closeAll();
      if (hadOpenPanel && restore) restore.focus();
      return;
    }

    if (!(event.target instanceof Element) || !this.contains(event.target)) return;

    if (event.key === 'ArrowRight') {
      const toggle = event.target.closest('[data-click-nav-toggle]');
      if (!toggle) return;
      if (toggle.getAttribute('aria-expanded') !== 'true') this.toggle(toggle);
      this.focusFirst(this.panelFor(toggle));
      event.preventDefault();
      return;
    }

    if (event.key === 'ArrowLeft') {
      const panel = event.target.closest('[data-click-nav-panel]');
      if (!panel) return;
      const toggle = this.parentToggleFor(panel);
      if (!toggle) return;
      this.closeToggle(toggle);
      this.lastToggle = this.deepestOpenToggle();
      this.updateScrim();
      toggle.focus();
      event.preventDefault();
      return;
    }

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (this.moveFocus(event.target, event.key === 'ArrowDown' ? 1 : -1)) {
        event.preventDefault();
      }
    }
  }

  moveFocus(target, direction) {
    const panel = target.closest('[data-click-nav-panel]');
    if (!panel) return false;
    const controls = this.focusableControls(panel);
    const index = controls.indexOf(target);
    const next = controls[index + direction];
    if (!next) return false;
    next.focus();
    return true;
  }

  focusFirst(panel) {
    const [first] = this.focusableControls(panel);
    if (first) first.focus();
  }

  focusableControls(panel) {
    if (!panel) return [];
    return [...panel.querySelectorAll('a[href], button:not([disabled])')];
  }

  closeAll() {
    for (const toggle of this.toggles()) {
      if (toggle.getAttribute('aria-expanded') === 'true') this.closeToggle(toggle);
    }
    this.updateScrim();
  }

  hasOpenPanel() {
    return this.querySelector('[data-click-nav-toggle][aria-expanded="true"]') !== null;
  }

  deepestOpenToggle() {
    const open = this.querySelectorAll('[data-click-nav-toggle][aria-expanded="true"]');
    return open.length ? open[open.length - 1] : null;
  }

  updateScrim() {
    if (!document.body) return;
    const navigations = document.querySelectorAll('click-nav');
    let anyOpen = false;
    for (const navigation of navigations) {
      const open = navigation.hasOpenPanel();
      navigation.closest('.section-header-navigation')?.classList.toggle('click-nav-section-open', open);
      if (open) anyOpen = true;
    }
    document.body.classList.toggle(OPEN_BODY_CLASS, anyOpen);
  }
}

if (!customElements.get('click-nav')) {
  customElements.define('click-nav', ClickNav);
}

class ClickStickyHeader extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.section-header-navigation');
    if (!this.section) return;
    this.section.classList.add('click-nav-sticky-section');

    this.controller = new AbortController();
    const { signal } = this.controller;
    this.stickyType = this.dataset.stickyType;
    this.currentScrollTop = window.scrollY;
    this.setHeaderHeight();

    if (this.stickyType === 'always') {
      this.section.classList.add('shopify-section-header-sticky');
    }

    window.addEventListener('scroll', this.onScroll.bind(this), { signal, passive: true });
    window.addEventListener('resize', this.setHeaderHeight.bind(this), { signal });
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.section?.classList.remove('click-nav-sticky-section');
  }

  setHeaderHeight() {
    if (!this.section) return;
    document.documentElement.style.setProperty('--header-height', `${this.section.offsetHeight}px`);
  }

  onScroll() {
    if (this.stickyType === 'always') return;

    const scrollTop = window.scrollY;
    const sectionTop = this.section.offsetTop;
    const sectionBottom = sectionTop + this.section.offsetHeight;

    if (scrollTop > this.currentScrollTop && scrollTop > sectionBottom) {
      this.section.classList.add('shopify-section-header-hidden', 'shopify-section-header-sticky');
      this.section.classList.remove('shopify-section-header-show');
      this.querySelector('click-nav')?.closeAll();
    } else if (scrollTop < this.currentScrollTop && scrollTop > sectionBottom) {
      this.section.classList.add('shopify-section-header-sticky', 'shopify-section-header-show', 'animate');
      this.section.classList.remove('shopify-section-header-hidden');
    } else if (scrollTop <= sectionTop) {
      this.section.classList.remove(
        'shopify-section-header-hidden',
        'shopify-section-header-show',
        'shopify-section-header-sticky',
        'animate'
      );
    }

    this.currentScrollTop = scrollTop;
  }
}

if (!customElements.get('click-sticky-header')) {
  customElements.define('click-sticky-header', ClickStickyHeader);
}
